import { Alert, Box, Stack, Typography } from '@mui/material'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { useEffect, useState } from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet } from '../generic/Api_Functions'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { resolveNotPlannedDescription } from './notPlannedReasonDescription'

/**
 * Story 192: the table of the details, with the Description of each row already resolved by its reasonCode
 * (CU7). It is mounted only inside the open dialog because `useDateFormatter` reads `dateFormats` off the MUI
 * theme: called at the top of the notice, it would make the notice itself depend on that theme (same reason
 * as `ReturnDateField`). The table, its columns and its data source do not change.
 */
const NotPlannedResourcesTable = ({ title, columns, rows }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const resolvedRows = rows.map((row) => ({ ...row, description: resolveNotPlannedDescription(row, t_, formatDateTime) }))
  return <SmartMateriaUITable title={title} columns={columns} dataFetch={resolvedRows} />
}

const NotPlannedResourcesAlert = (props) => {
  const { planificationId, unitCriteriaName, reload } = props
  const [openDialog, setOpenDialog] = useState(false)
  const [notPlannedResource, setNotPlannedResource] = useState(null)
  const { t_ } = useSmartTranslation()
  const columns = [
    {
      name: unitCriteriaName === 'Job' ? t_('Part') : t_('Fabrication Order'),
      field: 'resourceCode',
      readOnly: true
    },
    {
      name: t_('Job'),
      field: 'linkedJobReference',
      readOnly: true
    },
    {
      name: t_('Description'),
      field: 'description',
      readOnly: true
    },
    {
      name: t_('Planification date'),
      field: 'planificationDate',
      type: 'dateTimePicker'
    }
  ]

  useEffect(() => {
    const loadNotPlannedResources = async () => {
      if (!planificationId) return
      const data = await apiGet('allNotPlannedResources/' + planificationId)
      if (data) {
        setNotPlannedResource(data)
      } else {
        setNotPlannedResource(undefined)
      }
    }
    loadNotPlannedResources()
  }, [planificationId, reload])

  if (!notPlannedResource || notPlannedResource.length === 0) {
    return null
  }

  return (
    <>
      {/* Story 177: like the ribbon, the notice is as wide as its block (and never wider); nothing measures the window. */}
      <Box data-testid='not-planned-resources-alert' sx={{ maxWidth: '100%', minWidth: 0 }}>
        <Stack sx={{ width: '100%' }} spacing={2}>
          <Alert
            severity='error'
            sx={{
              '& .clickable': {
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'inherit'
              }
            }}
          >
            {t_('Error: Some resources have not been planned.')}{' '}
            <span
              className='clickable'
              onClick={() => {
                setOpenDialog(true)
              }}
            >
              {t_('Show details')}
            </span>
          </Alert>
        </Stack>
      </Box>
      <SmartDialog
        acceptCallback={setOpenDialog}
        closeCallback={() => {
          setOpenDialog(false)
        }}
        close
        accept
        cancel={false}
        renderComponent={
          <NotPlannedResourcesTable
            title={<Typography color='primary' style={{ width: '100%' }}>{t_('Not planned resources')}</Typography>}
            columns={columns}
            rows={notPlannedResource}
          />
            }
        setOpen={openDialog}
      />
    </>
  )
}

export default NotPlannedResourcesAlert
